import { useState, useEffect, useCallback } from 'react'
import api from '../utils/api'

/**
 * Custom hook to manage rotation state and actions for media objects
 * @param {string} mediaType - The type of media (song, album, playlist, mix, collage)
 * @param {number} mediaId - The ID of the media object
 * @returns {Object} - Rotation state and functions
 */
const useRotation = (mediaType, mediaId) => {
  const [inRotation, setInRotation] = useState(false)
  const [source, setSource] = useState(null) // 'collection' or 'queue'
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)

  // Check if the media is in the user's rotation
  const checkRotationStatus = useCallback(async () => {
    if (!mediaType || !mediaId) return

    try {
      const response = await api.get(`rotation-status/${mediaType}/${mediaId}/`)
      setInRotation(response.data.in_rotation)
      setSource(response.data.source)
      setError(null)
    } catch (err) {
      console.error('Error checking rotation status:', err)
      setError(err)
      // Don't change the inRotation state on error to avoid UI flickering
    }
  }, [mediaType, mediaId])

  // Toggle rotation status
  const toggleRotation = useCallback(async () => {
    if (!mediaType || !mediaId) return

    try {
      setIsLoading(true)
      setError(null)

      const response = await api.post('rotation/toggle/', {
        media_type: mediaType,
        media_id: mediaId,
      })

      // Update state based on response
      if (response.data.status === 'removed') {
        setInRotation(false)
        setSource(null)
      } else {
        setInRotation(true)
        setSource(response.data.source)
      }
    } catch (err) {
      console.error('Error toggling rotation:', err.response?.data || err)
      setError(err)

      // Show alert with error details if we have them
      if (err.response?.data?.error) {
        console.error(`Rotation error: ${err.response.data.error}`)
      }
    } finally {
      setIsLoading(false)
    }
  }, [mediaType, mediaId])

  // Check rotation status on mount
  useEffect(() => {
    checkRotationStatus()
  }, [checkRotationStatus])

  return {
    inRotation,
    source,
    isLoading,
    error,
    toggleRotation,
    checkRotationStatus,
  }
}

export default useRotation
